import { useEffect, useRef, useState } from "react";
import { LIGHTING_CHECK } from "../constants/interviewInstructions";

/**
 * Samples average brightness from a live <video> element.
 *
 * Draws the frame into a tiny offscreen canvas and averages Rec. 709 luma over
 * the pixels. Purely observational — it never touches the stream, the pose
 * validator, or step progression.
 *
 * @returns {{ luma: number|null, level: 'low'|'good'|'harsh'|null, isGood: boolean }}
 */
export const useLightingCheck = ({ videoRef, enabled }) => {
  const [luma, setLuma] = useState(null);
  const canvasRef = useRef(null);
  const samplesRef = useRef([]);

  useEffect(() => {
    if (!enabled) {
      samplesRef.current = [];
      setLuma(null);
      return undefined;
    }

    if (!canvasRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = LIGHTING_CHECK.SAMPLE_WIDTH;
      canvas.height = LIGHTING_CHECK.SAMPLE_HEIGHT;
      canvasRef.current = canvas;
    }

    const canvas = canvasRef.current;
    // willReadFrequently keeps the repeated getImageData on the CPU path
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const sample = () => {
      const video = videoRef?.current;
      // HAVE_CURRENT_DATA — anything less and the frame isn't paintable yet
      if (!video || !video.videoWidth || video.readyState < 2) return;

      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

        let total = 0;
        for (let i = 0; i < data.length; i += 4) {
          total +=
            0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        }
        const average = total / (data.length / 4);

        const samples = [...samplesRef.current, average].slice(
          -LIGHTING_CHECK.SMOOTHING_WINDOW
        );
        samplesRef.current = samples;
        setLuma(samples.reduce((sum, value) => sum + value, 0) / samples.length);
      } catch (error) {
        // drawImage/getImageData throw while a stream is being torn down or if
        // the frame is tainted — the reading is advisory, so skip this tick.
      }
    };

    sample();
    const intervalId = setInterval(sample, LIGHTING_CHECK.SAMPLE_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [enabled, videoRef]);

  // "blocked" must be tested before "low" — it is the darkest band.
  const level =
    luma == null
      ? null
      : luma < LIGHTING_CHECK.BLOCKED_MAX
      ? "blocked"
      : luma < LIGHTING_CHECK.DARK_MAX
      ? "low"
      : luma > LIGHTING_CHECK.BRIGHT_MIN
      ? "harsh"
      : "good";

  return { luma, level, isGood: level === "good" };
};

export default useLightingCheck;
